<template>
  <v-container fluid>
    <v-row>
      <v-col
        cols="12"
        md="6"
        align-self="center"
      >
        <div class="primary--text text-h5 my-2">
          {{ $t('group.title') }}
        </div>
      </v-col>
      <v-col
        v-if="$vuetify.breakpoint.smAndDown"
        cols="12"
        class="pt-0"
      >
        <span
          class="text-justify"
          style="white-space: pre-line; font-size: small;"
          v-text="$t('group.note')"
        />
      </v-col>
      <v-col
        cols="12"
        md="6"
        align-self="end"
        class="text-end"
      >
        <v-btn
          :loading="loading"
          color="primary"
          :block="$vuetify.breakpoint.smAndDown"
          class="text-capitalize"
          @click="createUserGroupDialog = true"
        >
          {{ $t('group.create_user_group') }}
        </v-btn>
        <v-dialog
          v-if="createUserGroupDialog"
          v-model="createUserGroupDialog"
          :width="$vuetify.breakpoint.mdAndUp ? '800px' : '100%'"
          persistent
        >
          <group-create-user-group-dialog
            @createUserGroupCancel="createUserGroupDialog = false"
            @createUserGroupSuccess="onPageRefresh(true); createUserGroupDialog = false"
          />
        </v-dialog>
      </v-col>
      <v-col
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="12"
        class="pt-0"
      >
        <span
          class="text-justify"
          style="white-space: pre-line; font-size: small;"
          v-text="$t('group.note')"
        />
      </v-col>
    </v-row>
    <v-row v-if="errorState">
      <v-col cols="12">
        <v-alert
          v-model="errorState"
          dense
          type="error"
          close-icon="mdi-reload"
          :dismissible="true"
          @input="$router.go(0)"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="groups"
          show-expand
          class="elevation-2 group-page"
          :expanded.sync="expandedList"
          :hide-default-footer="groups.length < 10"
          :items-per-page="10"
          @click:row="onRowClicked"
        >
          <template #[`expanded-item`]="{item}">
            <td :colspan="headers.length">
              <v-expand-transition>
                <group-page-group-table
                  :loading="loading"
                  :group="item"
                  @assignModule="onOpenAssignModuleDialog(item)"
                />
              </v-expand-transition>
            </td>
          </template>
          <template
            #[`item.data-table-expand`]="{ item }"
          >
            <v-icon
              v-if="item.page_groups.length > 0"
              :key="item.id"
              color="primary"
              class="v-data-table__expand-icon v-icon--link mdi mdi-chevron-down theme--light"
              :class="{ 'selectedItem' : activeItem && activeItem.id === item.id}"
            />
            <v-tooltip
              v-else
              top
            >
              <template #activator="{on, attrs}">
                <v-icon
                  color="primary"
                  class="v-data-table__expand-icon v-icon--link mdi mdi-plus theme--light"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <span>{{ $t('group.assign_module_to_user_group') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog
      v-if="assignGroupToModuleDialog"
      v-model="assignGroupToModuleDialog"
      :width="$vuetify.breakpoint.mdAndUp ? '60%' : '100%'"
      persistent
    >
      <assign-module-to-user-group
        :user-group="activeItem"
        @assignCompleted="onAssignModuleToUserGroupCompleted($event)"
        @assignCancel="onAssignModuleToUserGroupCancel"
      />
    </v-dialog>
  </v-container>
</template>

<script>

import GroupPageGroupTable from '../../components/GroupPageGroupTable'
import GroupCreateUserGroupDialog from '../../components/GroupCreateUserGroupDialog'
import AssignModuleToUserGroup from '../../components/AssignModuleToUserGroup'
import QUATSError from '../../plugins/error'

export default {
  components: {
    AssignModuleToUserGroup,
    GroupPageGroupTable, GroupCreateUserGroupDialog
  },
  data() {
    return {
      error: null,
      errorState: false,
      loading: true,
      groups: [],
      expandedList: [],
      createUserGroupDialog: false,
      activeItem: null,
      assignGroupToModuleDialog: false,
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('group.user_group_name'), value: this.$i18n.locale === 'en' ? 'name_en' : 'name_jp', class: 'primary white--text'},
        { text: this.$t('group.user_group_description'), value: this.$i18n.locale === 'en' ? 'desc_en' : 'desc_jp', class: 'primary white--text'},
        { text: '', value: 'data-table-expand', class: 'primary white--text'},
      ]
    }
  },

  mounted() {
    this.onPageRefresh()
  },

  methods: {
    onAssignModuleToUserGroupCancel() {
      this.assignGroupToModuleDialog = false
      this.activeItem = {}
    },

    onAssignModuleToUserGroupCompleted(item) {
      this.assignGroupToModuleDialog = false
      this.onPageRefresh(false, true)
      item.page_groups.length <= 0 ? this.expandedList = [] : this.expandedList = [item]
    },

    /***
     * @desc open assign module dialog with the correct data that was selected from user
     * @param item selected data
     */
    onOpenAssignModuleDialog(item) {
      this.activeItem = item
      this.assignGroupToModuleDialog = true
    },

    /***
     * @desc helper function that will trigger when table row was clicked
     * @param item data of selected row
     * @param event event that was fired from user
     */
    onRowClicked(item, event) {
      if(item.page_groups.length > 0) {
        if(event.isExpanded) {
          const index = this.expandedList.findIndex(i => i === item)
          this.expandedList.splice(index, 1)
          this.activeItem = {}
        } else{
          this.expandedList = []
          this.expandedList.push(item)
          this.activeItem = item
        }
      } else {
        this.onOpenAssignModuleDialog(item)
      }
    },

    /***
     * @desc helper function to refresh the page after data update or freshly open the page
     * @param afterGroupCreated pass true if this is called after update any data or simply ignore it if open page for first time
     * @param afterGroupPageUpdated
     */
    onPageRefresh(afterGroupCreated = false, afterGroupPageUpdated = false) {
      this.loading = true
      !afterGroupCreated || (this.createUserGroupDialog = false)
      const promises = [
        window.axios.get('/api/group/user-groups'),
      ]

      Promise.all(promises)
        .then((results) => {
          this.onGroupData(results[0].data.data)
          if(afterGroupCreated) {
            this.$store.commit('snackbar/successSnackbar', [this.$t('group.user_group_create_success')])
            this.$store.commit('snackbar/activateSnackbar', true)
          }else if(afterGroupPageUpdated) {
            this.$store.commit('snackbar/successSnackbar', [this.$t('group.user_group_page_updated_success')])
            this.$store.commit('snackbar/activateSnackbar', true)
          }
          this.errorState = false
        }).catch(err => {
          this.error = new QUATSError(this.$i18n, err).message
          this.errorState = true
        }).finally(() => {
          this.loading = false
        })
    },
    onGroupData(data) {
      data.forEach(x => {
        x.name_en = x.name_en || x.name_jp
        x.desc_en = x.desc_en || x.desc_jp
        x.page_groups.forEach(y => {
          y.name_en = y.name_en || y.name_jp
          y.desc_en = y.desc_en || y.desc_jp
        })
      })
      this.groups = data
    }
  }
}
</script>

<style scoped>
::v-deep .v-data-table>.v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none;
  background-color: rgba(238, 238, 238, 0.70);
}
::v-deep .group-page .v-data-table__wrapper .v-data-table-header th.sortable .v-data-table-header__icon {
  color: white !important;
}
.selectedItem {
  transform: rotate(-180deg);
}
</style>
